import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import {
  DOCUMENT_SUGGESTION_BANNER_DISMISS_AT_LEAST,
  DOCUMENT_SUGGESTION_BANNER_UNENGAGED_GAP_AT_LEAST,
  isModalClosed,
} from '@/lib/modalClosedRules';
import { ModalId, ModalViewAction } from '@heidi/schema/ml-scribe';
import { useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useCallback, useEffect, useRef } from 'react';

/** Re-export rule-aligned constants from `@/lib/modalClosedRules` (single source of truth). */
export {
  DOCUMENT_SUGGESTION_BANNER_DISMISS_AT_LEAST as DOCUMENT_SUGGESTION_BANNER_ACTIVE_DISMISS_THRESHOLD,
  DOCUMENT_SUGGESTION_BANNER_COOLDOWN_DAYS,
  DOCUMENT_SUGGESTION_BANNER_UNENGAGED_GAP_AT_LEAST as DOCUMENT_SUGGESTION_BANNER_UNENGAGED_THRESHOLD,
} from '@/lib/modalClosedRules';

/**
 * sessionStorage key holding the most recent session ID where the banner was
 * shown but the user has not yet engaged or fully dismissed. When a *different*
 * session later sees the banner we count the previous session as abandoned and
 * pad `view_count` (no `action`) on the auto-suppression `ModalId`, leaving
 * `consecutive_dismiss_count` untouched so the two thresholds stay independent.
 *
 * Uses sessionStorage rather than localStorage on purpose — pending state
 * shouldn't outlive the browser tab. If the user closes the tab without acting,
 * the in-flight "pending" gets dropped (same effect as engaging: nothing
 * persists). Cross-device suppression is fully driven by `seen_modals`.
 */
export const PENDING_SHOWN_SESSION_STORAGE_KEY =
  'heidi.documentSuggestionBanner.pendingShownSessionId';

/**
 * SSR-safe sessionStorage adapter. During server render there is no `window`,
 * so reads short-circuit to the atom's initial value and writes are no-ops.
 * Mirrors the localStorage helper in `src/atoms/evidenceTrialQueryCountAtom.ts`.
 */
const ssrSafeSessionStorage = createJSONStorage<string | null>(() => {
  if (typeof window === 'undefined') {
    return {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
    };
  }
  return window.sessionStorage;
});

/**
 * Co-located with the hook because no other consumer needs it; lives in
 * sessionStorage so it auto-clears on tab close.
 */
export const pendingShownSessionIdAtom = atomWithStorage<string | null>(
  PENDING_SHOWN_SESSION_STORAGE_KEY,
  null,
  ssrSafeSessionStorage
);

export type SuppressionTriggerReason =
  | 'auto_dismissed' // The user X'd every suggestion in a session, hitting the threshold.
  | 'auto_unengaged' // The user opened a new session while a previous one sat unengaged.
  | 'manual'; // The user clicked "Hide these suggestions".

interface UseDocumentSuggestionBannerSuppressionOptions {
  /** Fires once when a state mutation transitions the banner into the cooldown window. */
  onCooldownTriggered?: (reason: SuppressionTriggerReason) => void;
}

interface UseDocumentSuggestionBannerSuppressionResult {
  /** True while either the auto or manual rule says the banner should stay closed. */
  isSuppressed: boolean;
  /** True until the user query has hydrated; callers gate `shouldShow` on this. */
  isLoading: boolean;
  /** Mark the banner as visible for this session (idempotent per-session). */
  recordShown: (sessionId: string) => void;
  /** User clicked a CTA — clears the pending pointer; engagement resets the auto streak. */
  recordEngaged: (sessionId: string) => void;
  /** User dismissed every suggestion in this session. */
  recordAllDismissed: (sessionId: string) => void;
  /** User explicitly opted out via the "Hide these suggestions" control. */
  recordManualHide: (sessionId: string) => void;
}

/**
 * Tracks how the user has been treating the document-suggestion banner across
 * sessions and decides when to step out of their way. Suppression state is
 * persisted on a single `DOCUMENT_SUGGESTION_BANNER` `seen_modals` record so
 * it syncs across devices and survives reloads.
 *
 * Three suppression triggers, all routed through the same `ModalView`:
 *  1. **Active dismiss** (`auto_dismissed`) — `consecutive_dismiss_count >= 2`,
 *     30-day cooldown. Bumped on `recordAllDismissed`.
 *  2. **Lifetime non-engagement** (`auto_unengaged`) —
 *     `view_count - engage_count >= 4`, 30-day cooldown. Walkaways pad
 *     `view_count` only via the cross-session abandonment effect (no `action`,
 *     so `consecutive_dismiss_count` stays untouched). Active dismiss bumps
 *     `consecutive_dismiss_count` only — not `view_count` — so the two signals
 *     stay independent.
 *  3. **Manual hide** (`manual`) — permanent. `recordManualHide` flips
 *     `hide_forever: true` on the same record; `evaluateClosedRule` honors it
 *     unconditionally.
 *
 * Engaging (CTA click) resets `consecutive_dismiss_count` via `action: 'engage'`.
 */
export function useDocumentSuggestionBannerSuppression(
  sessionId: string | undefined,
  options: UseDocumentSuggestionBannerSuppressionOptions = {}
): UseDocumentSuggestionBannerSuppressionResult {
  const { onCooldownTriggered } = options;

  const { data: userData, isLoading } = useQueryUser((user) => {
    if (!user) return undefined;
    return {
      view: user.seen_modals?.[ModalId.EVIDENCE_DOCUMENT_SUGGESTION_BANNER],
    };
  });

  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();

  const [pendingShownSessionId, setPendingShownSessionId] = useAtom(
    pendingShownSessionIdAtom
  );

  const onCooldownTriggeredRef = useRef(onCooldownTriggered);
  useEffect(() => {
    onCooldownTriggeredRef.current = onCooldownTriggered;
  });

  const isSuppressed = isModalClosed(
    ModalId.EVIDENCE_DOCUMENT_SUGGESTION_BANNER,
    userData?.view ?? null
  );

  const consecutiveDismissCount = userData?.view?.consecutive_dismiss_count ?? 0;
  const viewCount = userData?.view?.view_count ?? 0;
  const engageCount = userData?.view?.engage_count ?? 0;
  const unengagedCount = viewCount - engageCount;

  // Promote the previous "shown but never engaged" session to a view-only bump
  // when a different session sees the banner. We deliberately omit `action` so
  // the optimistic update only touches `view_count` — `consecutive_dismiss_count`
  // remains the *active*-dismiss signal alone. The atom auto-syncs to
  // sessionStorage so reads stay consistent across remounts.
  useEffect(() => {
    if (!sessionId) return;
    if (!pendingShownSessionId || pendingShownSessionId === sessionId) return;

    // Dropping the pending pointer first prevents repeat promotion if the
    // mutation is in flight when this effect re-runs (e.g. fast remounts).
    setPendingShownSessionId(null);

    // Skip if the rule is already closed — bumping while in cooldown
    // would push `last_seen_at` forward and re-extend the suppression window.
    if (isSuppressed) return;

    markModalAsViewed({
      modal_id: ModalId.EVIDENCE_DOCUMENT_SUGGESTION_BANNER,
      increment_count: true,
    });

    if (unengagedCount + 1 >= DOCUMENT_SUGGESTION_BANNER_UNENGAGED_GAP_AT_LEAST) {
      onCooldownTriggeredRef.current?.('auto_unengaged');
    }
  }, [
    sessionId,
    pendingShownSessionId,
    unengagedCount,
    isSuppressed,
    markModalAsViewed,
    setPendingShownSessionId,
  ]);

  const recordShown = useCallback(
    (id: string) => {
      if (!id) return;
      setPendingShownSessionId((prev) => (prev === id ? prev : id));
    },
    [setPendingShownSessionId]
  );

  const recordEngaged = useCallback(
    (id: string) => {
      if (!id) return;
      setPendingShownSessionId(null);
      markModalAsViewed({
        modal_id: ModalId.EVIDENCE_DOCUMENT_SUGGESTION_BANNER,
        action: ModalViewAction.engage,
        increment_count: true,
      });
    },
    [markModalAsViewed, setPendingShownSessionId]
  );

  const recordAllDismissed = useCallback(
    (id: string) => {
      if (!id) return;
      setPendingShownSessionId(null);
      markModalAsViewed({
        modal_id: ModalId.EVIDENCE_DOCUMENT_SUGGESTION_BANNER,
        action: ModalViewAction.dismiss,
        increment_count: true,
      });
      // `dismiss` bumps `consecutive_dismiss_count` only (not `view_count`).
      const nextConsecutive = consecutiveDismissCount + 1;
      if (nextConsecutive >= DOCUMENT_SUGGESTION_BANNER_DISMISS_AT_LEAST) {
        onCooldownTriggeredRef.current?.('auto_dismissed');
      }
    },
    [consecutiveDismissCount, markModalAsViewed, setPendingShownSessionId]
  );

  const recordManualHide = useCallback(
    (id: string) => {
      if (!id) return;
      setPendingShownSessionId(null);
      // Permanent dismiss — flips `hide_forever` on the existing record. We
      // intentionally omit `action` and `increment_count` so the counters stay
      // as-is; `evaluateClosedRule` honors `hide_forever` regardless.
      markModalAsViewed({
        modal_id: ModalId.EVIDENCE_DOCUMENT_SUGGESTION_BANNER,
        increment_count: false,
        hide_forever: true,
      });
      onCooldownTriggeredRef.current?.('manual');
    },
    [markModalAsViewed, setPendingShownSessionId]
  );

  return {
    isSuppressed,
    isLoading,
    recordShown,
    recordEngaged,
    recordAllDismissed,
    recordManualHide,
  };
}
